import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getServiceBySlug, SERVICES } from '@/data/servicesData';
import SEOHead from '@/components/seo/SEOHead';
import Breadcrumbs from '@/components/seo/Breadcrumbs';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { trackServiceViewed } from '@/lib/analytics';
import { 
  Printer, CheckCircle2, Star, ShieldCheck, Zap, HelpCircle, 
  ChevronDown, ChevronUp, ArrowRight, FileText, Sparkles, Clock, Calculator 
} from 'lucide-react';
import { 
  createBreadcrumbListSchema, 
  createSpeakable, 
  createImageObject 
} from '@/utils/schemaGenerator';

export default function ServicePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const service = getServiceBySlug(slug) || SERVICES[0];

  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [estPages, setEstPages] = useState(10);
  const [estCopies, setEstCopies] = useState(1);
  const [estColor, setEstColor] = useState('bw');
  const [estSize, setEstSize] = useState('A4');

  // Calculate price estimate
  const rate = estSize === 'A3' ? (estColor === 'color' ? 20 : 5) : (estColor === 'color' ? 10 : 2);
  const calculatedCost = estPages * estCopies * rate;

  // Track page view
  useState(() => {
    if (service) {
      trackServiceViewed(service.slug, service.title);
    }
  });

  // Schema Objects
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.title,
    "serviceType": service.category,
    "provider": {
      "@type": "LocalBusiness",
      "name": "Inchpaper Private Limited",
      "image": "https://print.inchpaper.com/inchpaper-logo.png",
      "telephone": "+91-7703960982",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Main Market, Sohna",
        "addressLocality": "Sohna",
        "addressRegion": "Haryana",
        "postalCode": "122103",
        "addressCountry": "IN"
      }
    },
    "areaServed": "IN",
    "description": service.metaDescription,
    "offers": {
      "@type": "Offer",
      "price": "2.00",
      "priceCurrency": "INR"
    }
  };

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": service.h1,
    "description": service.metaDescription,
    "brand": {
      "@type": "Brand",
      "name": "Inchpaper"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": service.rating,
      "reviewCount": service.reviewCount
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": service.faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  };

  const breadcrumbItems = [
    { label: 'Print Services', url: '/#all-services' },
    { label: service.h1, url: `/${service.slug}` }
  ];

  const handleStartPrint = () => {
    navigate(`/?service=${service.slug}#upload`);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-between antialiased selection:bg-red-100 selection:text-red-900">
      <SEOHead
        title={service.metaTitle}
        description={service.metaDescription}
        keywords={service.keywords}
        canonical={`https://print.inchpaper.com/${service.slug}`}
        schemas={[
          serviceSchema,
          productSchema,
          faqSchema,
          createBreadcrumbListSchema([
            { name: "Home", url: "/" },
            { name: "Printing Services", url: "/printing-services" },
            { name: service.h1, url: `/${service.slug}` }
          ]),
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": service.h1,
            "speakable": createSpeakable(["h1", "p"])
          }
        ]}
      />

      <Navbar />

      <main className="grow">
        {/* Hero Section */}
        <div className="bg-gradient-to-b from-red-950 via-slate-900 to-slate-900 text-white pt-8 pb-16 px-4 sm:px-6 lg:px-8 border-b border-slate-800">
          <div className="max-w-7xl mx-auto">
            
            <div className="mb-4">
              <Breadcrumbs items={breadcrumbItems} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center pt-2">
              <div className="lg:col-span-7 space-y-5">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/20 border border-red-500/30 text-red-300 text-xs font-semibold tracking-wide">
                  <Sparkles className="w-3.5 h-3.5 text-red-400" />
                  <span>{service.category} Service</span>
                </div>

                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
                  {service.h1}
                </h1>

                <p className="text-slate-300 text-base sm:text-lg leading-relaxed max-w-2xl">
                  {service.tagline}
                </p>

                {/* Rating & Trust Badges */}
                <div className="flex flex-wrap items-center gap-4 text-xs sm:text-sm text-slate-300 pt-1">
                  <div className="flex items-center gap-1.5 bg-slate-800/80 px-3 py-1.5 rounded-lg border border-slate-700/60">
                    <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                    <span className="font-bold text-white">{service.rating} / 5.0</span>
                    <span className="text-slate-400">({service.reviewCount} reviews)</span>
                  </div>

                  <div className="flex items-center gap-1.5 bg-slate-800/80 px-3 py-1.5 rounded-lg border border-slate-700/60">
                    <ShieldCheck className="w-4 h-4 text-emerald-400" />
                    <span>256-bit Encrypted Security</span>
                  </div>

                  <div className="flex items-center gap-1.5 bg-slate-800/80 px-3 py-1.5 rounded-lg border border-slate-700/60">
                    <Clock className="w-4 h-4 text-sky-400" />
                    <span>60-Sec Kiosk Print</span>
                  </div>
                </div>

                {/* Primary CTA button */}
                <div className="pt-4 flex flex-col sm:flex-row gap-3">
                  <Button 
                    onClick={handleStartPrint}
                    className="bg-[#7d0909] hover:bg-red-700 text-white font-bold text-base px-8 py-6 rounded-xl shadow-lg transition-all flex items-center justify-center gap-2"
                  >
                    <Printer className="w-5 h-5" />
                    <span>Upload & Print Now</span>
                  </Button>
                  
                  <a 
                    href="#price-calculator" 
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-sm font-semibold transition-colors border border-slate-700"
                  >
                    <Calculator className="w-4 h-4" />
                    <span>Estimate Price</span>
                  </a>
                </div>
              </div>

              {/* Quick Action Widget Card */}
              <div className="lg:col-span-5">
                <div className="bg-white rounded-2xl p-6 shadow-2xl text-slate-900 border border-slate-100">
                  <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Instant Order Summary</span>
                  </div>

                  <div className="py-4 space-y-3">
                    <div className="text-xs text-slate-600 font-medium">Included Specifications:</div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {service.paperOptions.map((opt, i) => (
                        <div key={i} className="flex items-center gap-1.5 bg-slate-50 p-2 rounded-lg text-slate-700 font-medium border border-slate-100">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                          <span className="truncate">{opt}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="pt-2">
                    <Button 
                      onClick={handleStartPrint}
                      className="w-full bg-[#7d0909] hover:bg-[#5e0707] text-white py-6 rounded-xl font-bold text-sm shadow-md flex items-center justify-center gap-2"
                    >
                      <Zap className="w-4 h-4 fill-amber-300 text-amber-300" />
                      <span>Start Instant Kiosk Upload</span>
                    </Button>
                    <p className="text-[11px] text-center text-slate-400 mt-2">
                      No minimum order amount required • Instant UPI payment
                    </p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Content Section (Structured for SEO & AI Overviews) */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            
            {/* Main Column */}
            <div className="lg:col-span-8 space-y-10">
              
              {/* Detailed Overview */}
              <article className="bg-white p-6 sm:p-8 rounded-2xl shadow-xs border border-slate-200/80 prose prose-slate max-w-none">
                <h2 className="text-2xl font-bold text-slate-900 tracking-tight mb-4 flex items-center gap-2">
                  <FileText className="w-6 h-6 text-[#7d0909]" />
                  <span>About {service.h1}</span>
                </h2>
                
                <div className="text-slate-700 text-sm sm:text-base leading-relaxed whitespace-pre-line">
                  {service.overview}
                </div>
              </article>

              {/* Key Features Grid */}
              <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-xs border border-slate-200/80">
                <h2 className="text-2xl font-bold text-slate-900 tracking-tight mb-6">
                  Key Features & Advantages
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {service.features.map((feat, i) => (
                    <div key={i} className="p-4 rounded-xl bg-slate-50 border border-slate-100 hover:border-red-200 transition-colors">
                      <div className="flex items-center gap-2 text-slate-900 font-semibold text-sm mb-1">
                        <CheckCircle2 className="w-4 h-4 text-[#7d0909] shrink-0" />
                        <span>{feat.title}</span>
                      </div>
                      <p className="text-slate-600 text-xs leading-relaxed pl-6">
                        {feat.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Process Guide ("How it Works") */}
              <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-xs border border-slate-200/80">
                <h2 className="text-2xl font-bold text-slate-900 tracking-tight mb-6">
                  How {service.h1} Works in 4 Easy Steps
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {service.process.map((p, i) => (
                    <div key={i} className="p-4 rounded-xl bg-slate-50 border border-slate-200/60 relative">
                      <span className="w-7 h-7 rounded-full bg-[#7d0909] text-white font-bold text-xs flex items-center justify-center mb-3 shadow-xs">
                        {p.step}
                      </span>
                      <h3 className="font-bold text-slate-900 text-sm mb-1">{p.title}</h3>
                      <p className="text-slate-600 text-xs leading-relaxed">{p.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Interactive Cost Estimator */}
              <div id="price-calculator" className="bg-white p-6 sm:p-8 rounded-2xl shadow-xs border border-slate-200/80">
                <h2 className="text-2xl font-bold text-slate-900 tracking-tight mb-2 flex items-center gap-2">
                  <Calculator className="w-6 h-6 text-[#7d0909]" />
                  <span>Instant Price Calculator</span>
                </h2>
                <p className="text-slate-500 text-xs sm:text-sm mb-6">
                  Calculate the exact cost of your print job before uploading. Transparent rates with zero hidden charges.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Total Pages</label>
                    <input 
                      type="number" 
                      min="1" 
                      max="1000" 
                      value={estPages}
                      onChange={(e) => setEstPages(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-full border border-slate-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-red-700 focus:outline-hidden"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Copies</label>
                    <input 
                      type="number" 
                      min="1" 
                      max="100" 
                      value={estCopies}
                      onChange={(e) => setEstCopies(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-full border border-slate-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-red-700 focus:outline-hidden"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Color Mode</label>
                    <select 
                      value={estColor} 
                      onChange={(e) => setEstColor(e.target.value)}
                      className="w-full border border-slate-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-red-700 focus:outline-hidden"
                    >
                      <option value="bw">Black & White (₹2/p)</option>
                      <option value="color">Full Color (₹10/p)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Paper Size</label>
                    <select 
                      value={estSize} 
                      onChange={(e) => setEstSize(e.target.value)}
                      className="w-full border border-slate-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-red-700 focus:outline-hidden"
                    >
                      <option value="A4">A4 Standard</option>
                      <option value="A3">A3 Large Format</option>
                    </select>
                  </div>
                </div>

                <div className="bg-red-50/80 border border-red-200 p-4 rounded-xl flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div>
                    <div className="text-xs text-slate-500 font-medium">Estimated Total Price</div>
                    <div className="text-2xl font-extrabold text-[#7d0909]">₹{calculatedCost}.00</div>
                    <div className="text-[11px] text-slate-500">
                      ({estPages} pages × {estCopies} copies @ ₹{rate}/page)
                    </div>
                  </div>

                  <Button 
                    onClick={handleStartPrint}
                    className="bg-[#7d0909] hover:bg-[#5e0707] text-white px-6 py-3 rounded-lg text-sm font-bold shadow-xs flex items-center gap-2"
                  >
                    <span>Proceed to Print</span>
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* FAQ Section */}
              <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-xs border border-slate-200/80">
                <h2 className="text-2xl font-bold text-slate-900 tracking-tight mb-2 flex items-center gap-2">
                  <HelpCircle className="w-6 h-6 text-[#7d0909]" />
                  <span>Frequently Asked Questions</span>
                </h2>
                <p className="text-slate-500 text-xs sm:text-sm mb-6">
                  Got questions about {service.h1.toLowerCase()}? Here are expert answers.
                </p>

                <div className="space-y-3">
                  {service.faqs.map((faq, i) => {
                    const isOpen = openFaqIndex === i;
                    return (
                      <div key={i} className="border border-slate-200 rounded-xl overflow-hidden bg-slate-50/50">
                        <button
                          onClick={() => setOpenFaqIndex(isOpen ? null : i)}
                          className="w-full flex items-center justify-between p-4 text-left font-semibold text-slate-900 text-sm hover:bg-slate-100 transition-colors"
                        >
                          <span className="pr-4">{faq.q}</span>
                          {isOpen ? <ChevronUp className="w-4 h-4 text-slate-500 shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" />}
                        </button>
                        {isOpen && (
                          <div className="px-4 pb-4 pt-1 text-slate-600 text-xs sm:text-sm leading-relaxed border-t border-slate-200/60 bg-white">
                            {faq.a}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

            </div>

            {/* Sidebar Column */}
            <div className="lg:col-span-4 space-y-6">
              
              {/* Other Print Services */}
              <div className="bg-white p-6 rounded-2xl shadow-xs border border-slate-200/80 sticky top-20">
                <h3 className="font-bold text-slate-900 text-base mb-4 border-b border-slate-100 pb-3">
                  Related Print Services
                </h3>

                <div className="space-y-2">
                  {SERVICES.filter(s => s.slug !== service.slug).slice(0, 8).map((rel) => (
                    <Link
                      key={rel.slug}
                      to={`/${rel.slug}`}
                      className="flex items-center justify-between p-2.5 rounded-lg text-xs font-medium text-slate-700 hover:bg-red-50 hover:text-red-800 transition-colors group"
                    >
                      <span className="truncate pr-2">{rel.h1}</span>
                      <ArrowRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-red-700 group-hover:translate-x-1 transition-all shrink-0" />
                    </Link>
                  ))}
                </div>

                <div className="pt-4 mt-4 border-t border-slate-100">
                  <Link 
                    to="/#all-services" 
                    className="text-xs font-bold text-[#7d0909] hover:underline flex items-center gap-1"
                  >
                    <span>View All 25 Services Catalog</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>

            </div>

          </div>
        </section>

        {/* Bottom CTA Banner */}
        <section className="bg-gradient-to-r from-[#7d0909] to-red-950 text-white py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center space-y-4">
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
              Ready to Print Your Document Instantly?
            </h2>
            <p className="text-red-100 text-sm sm:text-base max-w-xl mx-auto">
              Upload your files online, pay securely via UPI, and collect prints from your nearest Inchpaper kiosk in under 60 seconds.
            </p>
            <div className="pt-2">
              <Button 
                onClick={handleStartPrint}
                className="bg-white text-[#7d0909] hover:bg-slate-100 font-extrabold text-base px-8 py-6 rounded-full shadow-lg transition-transform hover:scale-105 inline-flex items-center gap-2"
              >
                <Printer className="w-5 h-5 text-[#7d0909]" />
                <span>Start Printing Now</span>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
